import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Badge, LoadingSkeleton, Pagination, EmptyState } from './ui';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

const ACTION_VARIANT = {
  create: 'success',
  update: 'info',
  delete: 'danger',
  restore: 'warning',
  login: 'default',
};

function formatResourceType(type) {
  return type ? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
}

function AuditLog({ token }) {
  const [logs, setLogs] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const pageSize = 25;

  useEffect(() => {
    fetch('/api/v1/audit-logs/resource-types', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setResourceTypes)
      .catch(() => {});
  }, [token]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (actionFilter) params.set('action', actionFilter);
      if (resourceFilter) params.set('resource_type', resourceFilter);

      const response = await fetch(`/api/v1/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setTotalPages(data.total_pages);
      } else {
        setError('Failed to load activity log');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [token, page, actionFilter, resourceFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [actionFilter, resourceFilter]);

  const columns = [
    {
      header: 'When',
      render: (row) => (
        <span style={{ whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleString()}</span>
      ),
    },
    {
      header: 'User',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.username || 'System'}</div>
          {row.role && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{row.role}</div>}
        </div>
      ),
    },
    {
      header: 'Action',
      render: (row) => <Badge variant={ACTION_VARIANT[row.action] || 'default'}>{row.action}</Badge>,
    },
    { header: 'Resource', render: (row) => formatResourceType(row.resource_type) },
    { header: 'Details', render: (row) => row.resource_label || (row.resource_id ? `#${row.resource_id}` : '—') },
    {
      header: '',
      render: (row) => row.details && (
        <button
          onClick={() => setExpanded(expanded === row.id ? null : row.id)}
          style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}
        >
          {expanded === row.id ? 'Hide' : 'View'}
        </button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Activity Log</h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '0.85rem', background: 'var(--color-surface)' }}
          >
            <option value="">All actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="restore">Restore</option>
            <option value="login">Login</option>
          </select>
          <select
            value={resourceFilter}
            onChange={(e) => setResourceFilter(e.target.value)}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '0.85rem', background: 'var(--color-surface)' }}
          >
            <option value="">All resource types</option>
            {resourceTypes.map((t) => (
              <option key={t} value={t}>{formatResourceType(t)}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <Card>
        {loading ? (
          <LoadingSkeleton type="text" rows={10} />
        ) : logs.length === 0 ? (
          <EmptyState
            title="No activity recorded yet"
            message="Actions taken across the system will show up here."
            icon={<ClipboardDocumentListIcon />}
          />
        ) : (
          <>
            <Table columns={columns} data={logs} />
            {logs.filter((l) => l.id === expanded).map((l) => (
              <pre
                key={l.id}
                style={{
                  marginTop: '1rem', padding: '0.75rem', background: 'var(--color-bg)',
                  borderRadius: 'var(--radius)', fontSize: '0.75rem', overflowX: 'auto',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                }}
              >
                {JSON.stringify(JSON.parse(l.details), null, 2)}
              </pre>
            ))}
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}

export default AuditLog;
